import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { listUnreadNotifications, markNotificationRead } from "../api/notifications";
import { useAuth } from "./useAuth";

const NOTIFICATION_POLL_INTERVAL_MS = 30000;

export function useNotificationBell() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const unreadQuery = useQuery({
    queryKey: ["notifications", "unread", user?.id],
    queryFn: listUnreadNotifications,
    enabled: Boolean(user),
    staleTime: NOTIFICATION_POLL_INTERVAL_MS,
    refetchOnWindowFocus: false,
    refetchInterval: () => {
      if (typeof document === "undefined") {
        return false;
      }
      return document.visibilityState === "visible" ? NOTIFICATION_POLL_INTERVAL_MS : false;
    },
  });

  const markReadMutation = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["notifications", "unread"] }),
        queryClient.invalidateQueries({ queryKey: ["notifications", "list"] }),
        queryClient.invalidateQueries({ queryKey: ["notifications", "my-comments"] }),
      ]);
    },
  });

  return {
    unreadCount: unreadQuery.data?.count ?? 0,
    commentUnreadCount: unreadQuery.data?.comment_count ?? 0,
    recentNotifications: unreadQuery.data?.results ?? [],
    isLoading: unreadQuery.isLoading,
    markAsRead: async (notificationId: number | string) => markReadMutation.mutateAsync(notificationId),
  };
}
